/**
 * COOP-safe OAuth popup communication bridge.
 *
 * When Cross-Origin-Opener-Policy severs window.opener, postMessage and popup.closed
 * checks fail. This module uses localStorage + storage events and BroadcastChannel
 * as reliable fallbacks that work across same-origin popup windows.
 */
export const OAUTH_STATE_KEY = 'social_login_oauth_pending';
export const OAUTH_POPUP_SESSION_KEY = 'social_login_oauth_popup';
export const OAUTH_RESULT_KEY_PREFIX = 'social_login_oauth_result_';
export const OAUTH_DELIVERED_KEY = 'social_login_oauth_delivered';
export const POPUP_WINDOW_NAMES = new Set([
    'OAuth2Login',
    'XLogin',
    'Google Sign In',
    'Authorization',
    'TelegramLogin',
]);
export function getOAuthResultKey(nonce) {
    return `${OAUTH_RESULT_KEY_PREFIX}${nonce}`;
}
export function getBroadcastChannelName(provider, nonceOrState) {
    if (provider === 'oauth2')
        return `oauth2_${nonceOrState}`;
    if (provider === 'twitter')
        return `twitter_oauth_${nonceOrState}`;
    if (provider === 'telegram')
        return `telegram_oauth_${nonceOrState}`;
    return `google_oauth_${nonceOrState}`;
}
export function markOAuthPopup(nonce) {
    try {
        sessionStorage.removeItem(OAUTH_DELIVERED_KEY);
        sessionStorage.setItem(OAUTH_POPUP_SESSION_KEY, nonce);
    }
    catch (_a) {
        // sessionStorage may be unavailable in some contexts
    }
}
export function clearOAuthPopupMarker() {
    try {
        sessionStorage.removeItem(OAUTH_POPUP_SESSION_KEY);
    }
    catch (_a) {
        // ignore
    }
}
export function hasOAuthRedirectParams() {
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return (search.has('code') ||
        search.has('error') ||
        (search.has('hash') && search.has('auth_date')) ||
        hash.has('access_token') ||
        hash.has('id_token') ||
        hash.has('error'));
}
export function isOAuthPopupContext() {
    if (typeof window === 'undefined')
        return false;
    return !!window.opener || POPUP_WINDOW_NAMES.has(window.name) || !!sessionStorage.getItem(OAUTH_POPUP_SESSION_KEY);
}
export function shouldAutoFinishOAuthRedirect() {
    if (!localStorage.getItem(OAUTH_STATE_KEY))
        return false;
    // Popup only. Same-window redirect (flow: 'redirect') must call handleRedirectCallback().
    return isOAuthPopupContext();
}
/**
 * Deliver OAuth result from popup to opener via all available channels.
 */
export function deliverOAuthResult(provider, nonceOrState, message) {
    // Prevent duplicate delivery when both eager handler and plugin constructor run
    try {
        if (sessionStorage.getItem(OAUTH_DELIVERED_KEY))
            return;
        sessionStorage.setItem(OAUTH_DELIVERED_KEY, '1');
    }
    catch (_a) {
        // sessionStorage unavailable
    }
    // postMessage when opener reference is still available
    try {
        if (window.opener) {
            window.opener.postMessage(message, window.location.origin);
        }
    }
    catch (_b) {
        // COOP may block opener access
    }
    // BroadcastChannel works across same-origin windows without opener
    if (nonceOrState) {
        try {
            const channel = new BroadcastChannel(getBroadcastChannelName(provider, nonceOrState));
            channel.postMessage(message);
            channel.close();
        }
        catch (_c) {
            // BroadcastChannel not supported
        }
    }
    // localStorage + storage event is the most COOP-resilient fallback
    if (nonceOrState) {
        try {
            localStorage.setItem(getOAuthResultKey(nonceOrState), JSON.stringify(Object.assign(Object.assign({}, message), { _ts: Date.now() })));
        }
        catch (_d) {
            // localStorage may be full or unavailable
        }
    }
}
/**
 * Listen for OAuth results from popup via postMessage, BroadcastChannel, and localStorage.
 * Returns a cleanup function.
 */
export function listenForOAuthResult(provider, nonceOrState, handlers) {
    const resultKey = getOAuthResultKey(nonceOrState);
    const channelName = getBroadcastChannelName(provider, nonceOrState);
    let broadcastChannel = null;
    try {
        broadcastChannel = new BroadcastChannel(channelName);
    }
    catch (_a) {
        // BroadcastChannel not supported
    }
    const processData = (data) => {
        if ((data === null || data === void 0 ? void 0 : data.source) && String(data.source).startsWith('angular'))
            return false;
        if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-response') {
            handlers.onResponse(data);
            return true;
        }
        if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-error') {
            handlers.onError(data.error || 'User cancelled the OAuth flow', data.code);
            return true;
        }
        return false;
    };
    const handleMessage = (event) => {
        if (event.origin !== window.location.origin)
            return;
        processData(event.data);
    };
    const handleStorage = (event) => {
        if (event.key !== resultKey || !event.newValue)
            return;
        try {
            const data = JSON.parse(event.newValue);
            localStorage.removeItem(resultKey);
            processData(data);
        }
        catch (_a) {
            // ignore parse errors
        }
    };
    const handleBroadcast = (event) => {
        processData(event.data);
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    if (broadcastChannel) {
        broadcastChannel.onmessage = handleBroadcast;
    }
    // Poll localStorage as fallback when storage event is missed (e.g. same-window writes)
    const pollInterval = window.setInterval(() => {
        try {
            const raw = localStorage.getItem(resultKey);
            if (!raw)
                return;
            localStorage.removeItem(resultKey);
            const data = JSON.parse(raw);
            processData(data);
        }
        catch (_a) {
            // ignore
        }
    }, 300);
    return () => {
        window.removeEventListener('message', handleMessage);
        window.removeEventListener('storage', handleStorage);
        clearInterval(pollInterval);
        if (broadcastChannel) {
            broadcastChannel.close();
        }
        try {
            localStorage.removeItem(resultKey);
        }
        catch (_a) {
            // ignore
        }
    };
}
//# sourceMappingURL=oauth-popup-bridge.js.map